<template>
  <div class="p-family-club__form-container">
    <form autocomplete="off" @submit.prevent="onSubmitForm">
      <Loader v-if="isLoading"></Loader>
      <Alert
        v-if="!isLoading && errorMsgs.length"
        key="submit-errors"
        dismiss-by-native=""
        type="danger"
        :messages="errorMsgs"
        @close="removeErrors"></Alert>

      <div class="e-status__container">
        <div class="e-status e-status--active">
          {{ dictionary.activeStatusText }}
        </div>
        <div class="e-status">{{ dictionary.disabledStatusText }}</div>
      </div>
      <div class="b-block b-block--transparent b-block--sm-pad">
        <div class="b-block__form-section b-block__form-section--mb-0">
          <div class="b-block__mini-title">
            {{ dictionary.personalInfoTitle }}
          </div>

          <!-- row -->
          <div class="form-floating">
            <input
              id="firstName"
              v-model.trim="firstName"
              name="firstName"
              type="text"
              class="form-control"
              :placeholder="dictionary.firstNamePlaceholder"
              :class="{ 'form-control--error': v$.firstName.$error }"
              @blur="v$.firstName.$touch" />

            <label for="firstName" class="form-label">
              {{ dictionary.firstNameLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.firstName.$error">
              <div
                v-if="v$.firstName.required.$invalid"
                class="field-validation-error">
                {{ dictionary.firstNameRequired }}
              </div>
              <div
                v-if="
                  v$.firstName.minLength.$invalid ||
                  v$.firstName.maxLength.$invalid
                "
                class="field-validation-error">
                {{ dictionary.firstNameLength }}
              </div>
              <div
                v-if="v$.firstName.regex.$invalid"
                class="field-validation-error">
                {{ dictionary.firstNameAlpha }}
              </div>
            </template>
          </div>

          <!-- row -->
          <div class="form-floating">
            <input
              id="lastName"
              v-model.trim="lastName"
              name="lastName"
              type="text"
              class="form-control"
              :placeholder="dictionary.lastNamePlaceholder"
              :class="{ 'form-control--error': v$.lastName.$error }"
              @blur="v$.lastName.$touch" />

            <label for="lastName" class="form-label">
              {{ dictionary.lastNameLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.lastName.$error">
              <div
                v-if="v$.lastName.required.$invalid"
                class="field-validation-error">
                {{ dictionary.lastNameRequired }}
              </div>
              <div
                v-if="
                  v$.lastName.minLength.$invalid ||
                  v$.lastName.maxLength.$invalid
                "
                class="field-validation-error">
                {{ dictionary.lastNameLength }}
              </div>
              <div
                v-if="v$.lastName.regex.$invalid"
                class="field-validation-error">
                {{ dictionary.lastNameAlpha }}
              </div>
            </template>
          </div>

          <!-- row -->
          <div class="form-floating">
            <input
              id="email"
              v-model.trim="email"
              name="email"
              type="email"
              class="form-control"
              :placeholder="dictionary.emailPlaceholder"
              :class="{ 'form-control--error': v$.email.$error }"
              formnovalidate
              @blur="v$.email.$touch" />

            <label for="email" class="form-label">
              {{ dictionary.emailLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.email.$error">
              <div
                v-if="v$.email.required.$invalid"
                class="field-validation-error">
                {{ dictionary.emailRequired }}
              </div>
              <div
                v-if="v$.email.email.$invalid"
                class="field-validation-error">
                {{ dictionary.emailRegex }}
              </div>
            </template>
          </div>

          <div class="p-contact__form-message">
            {{ dictionary.fieldRequired }}
          </div>
        </div>
      </div>

      <div class="b-block__form-btn">
        <button
          type="submit"
          class="p-contact__submit-btn"
          :disabled="submitButtonIsDisabled">
          <span>{{ dictionary.buttonSubmitText }}</span>
        </button>
      </div>
    </form>

    <ModalOverlay
      v-model="showSuccessModal"
      background-class="b-pop-up--alert-success b-pop-up__holder"
      header-background-class="b-pop-up__header">
      <template #header>
        <div class="b-pop-up__header__title">
          {{ dictionary.submitSuccessHeader }}
        </div>
      </template>
      <template #body>
        <div class="b-pop-up__alert-success__content">
          <div
            class="b-pop-up__alert-success__icon b-pop-up__alert-success__icon--success"></div>
          <div class="b-pop-up__alert-success__content__container">
            <div class="b-pop-up__alert-success__title">
              {{ dictionary.submitSuccessTitle }}
            </div>
            <div class="b-pop-up__alert-success__text">
              {{ dictionary.submitSuccessContent }}
            </div>

            <Link
              v-if="homePageLink"
              class="b-pop-up__alert-success__cta"
              :url="homePageLink.url"
              :target="homePageLink.target"
              :title="homePageLink.title" />
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script>
import * as lib from './../../lib/lib'
import constants from '../../lib/constants'
import Loader from './../components/Shared/Loader.vue'
import Alert from './../components/Shared/Alert.vue'
import useVuelidate from '@vuelidate/core'
import ModalOverlay from './../components/ModalOverlay.vue'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'
import Link from './../components/UI/Link.vue'

import {
  email,
  helpers,
  maxLength,
  minLength,
  required,
} from '@vuelidate/validators'

const { greekLatin } = constants.regex

export default {
  /* eslint-disable vue/no-reserved-component-names */
  components: { Loader, ModalOverlay, Alert, Link },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      isLoading: true,
      errorMsgs: [],
      showSuccessModal: false,
      // personal info
      firstName: '',
      lastName: '',
      email: '',
      homePageLink: null,
    }
  },
  validations() {
    return {
      // personal info
      firstName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
        regex: helpers.regex(greekLatin),
      },
      lastName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
        regex: helpers.regex(greekLatin),
      },
      email: {
        required,
        email,
      },
    }
  },

  computed: {
    submitButtonIsDisabled() {
      return this.isLoading || this.v$.$invalid
    },
  },

  mounted() {
    const { homePageLink } = lib.getData(
      'familyClubUserRegisterConfirmEmailFormAppData'
    )

    this.homePageLink = homePageLink

    lib.checkCaptcha(() => (this.isLoading = false))
  },

  methods: {
    removeErrors() {
      this.errorMsgs = []
    },
    toggleSuccessModal(show) {
      this.showSuccessModal = show
    },

    resetDefaultFormData() {
      document.activeElement.blur()
      this.v$.$reset()
      this.firstName = ''
      this.lastName = ''
      this.email = ''
    },

    async onSubmitForm() {
      this.v$.$validate()

      if (this.isLoading) {
        return false
      }

      if (this.v$.$invalid) {
        await this.$nextTick()
        lib.scrollToValidation()
        return false
      }

      this.removeErrors()
      this.isLoading = true

      const postData = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        returnUrlInfo: this.configuration.returnUrlInfo,
        language: this.configuration.language,
      }
      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointSubmitData,
        successCallback: this.handleSuccess,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.captchaAction,
      })
    },

    handleSuccess() {
      this.isLoading = false
      this.errorMsgs = []
      this.toggleSuccessModal(true)
      this.resetDefaultFormData()
    },

    async handleError(messages) {
      this.isLoading = false

      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage
      )

      await this.$nextTick()
      lib.scrollToError()
    },
  },
}
</script>
